/**
 * useAnsweredElsewhere — "this surface WAS showing a decision, and it cleared
 * without this surface answering it."
 *
 * emuw-v1-5 `shared-decision-components` Phase 3 task 10.
 *
 * The behaviour it exists to fix: A answers on one surface, the push frame
 * converges, `hitl_question` goes null on B — and both hosts today collapse the
 * whole section to `null`. B's card silently vanishes from under the AM's cursor
 * mid-read, with nothing said about why.
 *
 * SEPARATE FROM `useHitlSubmitLatch`'s `refusal`, and the separation is the point.
 * A refusal means THIS surface submitted and was told no. This hook covers the
 * path where this surface never submitted at all. Conflating them would make one
 * of the two cases unrepresentable.
 *
 * THE TRANSIENT NULL FRAME IS THE WHOLE DIFFICULTY. The add-in's post-submit
 * `authoritative: true` burst poll drops `hitl_question` for a frame on a decision
 * that is still perfectly open. Treating that as answered-elsewhere would flash an
 * "already answered" card at an AM who is mid-read on a live decision — the mirror
 * image of the bug being fixed, and arguably worse because it is actively false.
 *
 * So the release rule is copied VERBATIM from `useHitlSubmitLatch`: latch on the
 * question's content key; clear only when a genuinely DIFFERENT, non-null key
 * arrives, or the instance changes. Reused rather than re-derived so the two
 * latches cannot drift apart.
 */

import { useEffect, useRef, useState } from "react";

import { hitlQuestionKey } from "./hitlQuestionKey";

export interface UseAnsweredElsewhereArgs {
  hitlQuestion: unknown;
  instanceId: string | null;
  /**
   * True while THIS surface's own submit is in flight, has been accepted (the
   * latch holds `isSubmitting`), or was refused.
   *
   * When true the hook returns false: that path already renders through
   * `refusal` or advances the run, and rendering both would double-render the
   * answered card.
   */
  submittedHere: boolean;
}

export function useAnsweredElsewhere({
  hitlQuestion,
  instanceId,
  submittedHere,
}: UseAnsweredElsewhereArgs): { answeredElsewhere: boolean } {
  const currentKey = hitlQuestion != null ? hitlQuestionKey(hitlQuestion) : null;
  // The last question this surface actually SHOWED. Only a key we have seen can
  // later be observed to have cleared.
  const shownKeyRef = useRef<string | null>(null);
  const [answeredElsewhere, setAnsweredElsewhere] = useState(false);

  // Reset everything when the drawer moves to a different run.
  useEffect(() => {
    shownKeyRef.current = null;
    setAnsweredElsewhere(false);
  }, [instanceId]);

  useEffect(() => {
    if (currentKey !== null) {
      if (shownKeyRef.current !== null && currentKey !== shownKeyRef.current) {
        // A genuinely different gate. Whatever happened to the previous one is
        // history now — clear, exactly as the submit latch does.
        setAnsweredElsewhere(false);
      }
      shownKeyRef.current = currentKey;
      return;
    }
    // currentKey === null. This is EITHER the decision resolving OR the transient
    // burst-poll frame — indistinguishable from here, which is why the latch
    // holds rather than deciding. It only becomes "answered elsewhere" if this
    // surface had a question up and did not itself answer it.
    if (shownKeyRef.current !== null && !submittedHere) {
      setAnsweredElsewhere(true);
    }
  }, [currentKey, submittedHere]);

  // `submittedHere` wins even if the flag was already set: the refusal path owns
  // the card from the moment this surface's own submit resolves.
  return { answeredElsewhere: answeredElsewhere && !submittedHere };
}

export default useAnsweredElsewhere;
